import { useFetcher } from "@remix-run/react";
import { useEffect, useMemo, useState } from "react";

import { stringIsEmpty } from "../bool-helpers";
import { Logger } from "../logger";
import { toQueryString } from "../object-helpers";

/**
 * Custom hook to fetch data by ID.
 *
 * @returns {object} An object data, loading state, and setter functions.
 */
function useFetchData<
  TResponseLoader,
  TData,
  TParams = Record<string, unknown>,
>(
  props: {
    urlPath: string; // URL path to fetch the data [base path: /data/*]

    nestedKeys?: string; // Nested keys for the data object [data: {data:employeeOne:{foo:bar}} ] [ex: "data.employeeOne"] [resp: {foo:bar}]

    initialData?: Partial<TData>; // Initial data hydrated from the route loader (avoids extra /data/* request on mount)
  } & Parameters<typeof useFetcher>[0],
): {
  data: Partial<TData>;
  loading: boolean;
  setReloadAgain: React.Dispatch<React.SetStateAction<boolean>>;
  setParams: React.Dispatch<React.SetStateAction<TParams | undefined>>;
} {
  const { nestedKeys, urlPath, initialData, ...fetchProps } = props ?? {};

  // Destructuring response data, loader function, and fetcher state from useFetcher hook
  const {
    data: responseData,
    load: loaderFn,
    state: fetcherState,
  } = useFetcher<TResponseLoader>(fetchProps);

  // State to other parameters to pass to the fetcher
  const [params, setParams] = useState<
    TParams & { size: number; page: number }
  >({ page: 0 });

  // State to control reloading
  const [reloadAgain, setReloadAgain] = useState(false);

  // State to control loading status
  const [loading, setLoading] = useState(true);

  // Memoized employee profile data to avoid unnecessary re-renders
  const data = useMemo<Partial<TData>>(() => {
    let response: any = responseData;

    if (typeof response != "object" || stringIsEmpty(nestedKeys))
      return response ?? initialData;

    for (const key of nestedKeys!.split(".")) {
      if (!response || typeof response != "object") break;

      response = response[key];
    }
    return response ?? initialData ?? {};
  }, [JSON.stringify(responseData), initialData]);

  // Effect to changes loading state based on fetcher state
  useEffect(() => {
    if (fetcherState != "idle") {
      setLoading(true);
      return;
    }

    const timeout = setTimeout(() => {
      setLoading(false);
    }, 500);

    return () => clearTimeout(timeout);
  }, [fetcherState != "idle"]);

  // Effect to fetch data on the first load and when dependencies (id, reloadAgain) change
  useEffect(() => {
    // If id is not valid or reloadAgain is false, exit the effect
    if (!reloadAgain) return;

    // Set a timeout to call loadData after 350ms
    const timeout = setTimeout(() => {
      loadData(3);
    }, 350);

    // Cleanup function to clear the timeout when the component unmounts or dependencies change
    return () => clearTimeout(timeout);
  }, [params, reloadAgain]);

  // Function to load data with a retry mechanism
  const loadData = (limit: number, count?: number) => {
    try {
      // Call the loader function with the constructed URL
      const searchQuery =
        !!params && typeof params == "object" && !Array.isArray(params)
          ? toQueryString(params)
          : "";

      loaderFn(urlPath + searchQuery);
      setReloadAgain(false);
    } catch (e) {
      // Log the error with the current retry count and limit
      Logger.error(e, { title: `load fetch id error ( ${count} / ${limit} )` });

      // Retry loading data if the retry count is less than the limit
      // loadData(limit, count ? count + 1 : 1);
    }
  };

  // Returning the relevant state and setter functions
  return {
    data,
    loading,
    setParams,
    setReloadAgain,
  };
}

export default useFetchData;
